/**
 * Who drives the robot now, read from the teleop_service and
 * rollout_service_worker heartbeats. The fleet viewer copies these rules in
 * apps/verne_web_app/frontend/src/controlSource.ts.
 */

import { createContext } from 'react';

import type { HeartbeatItem } from './api/types';

export type ControlSource = 'teleop' | 'policy';

export type ControlInfo = { source: ControlSource; checkpoint: string | null } | null;

/** Teleop engine states in which a person commands the followers. */
const TELEOP_DRIVING = new Set(['bridging', 'active']);
/** Rollout worker states in which the policy commands the arms. */
const POLICY_DRIVING = new Set(['starting', 'inferring']);

/** The admin app provides it; null (the default) draws no badge. */
export const ControlSourceContext = createContext<ControlInfo>(null);

function alivePayload(heartbeats: HeartbeatItem[], id: string): Record<string, unknown> {
  const hb = heartbeats.find((h) => h.heartbeat_id === id);
  // A dead heartbeat keeps its last payload.
  return hb?.status === 'alive' ? (hb.payload ?? {}) : {};
}

export function controlInfo(heartbeats: HeartbeatItem[]): ControlInfo {
  const teleop = alivePayload(heartbeats, 'teleop_service');
  // A DAgger takeover pauses the rollout, so teleop is checked first.
  if (TELEOP_DRIVING.has(String(teleop.state))) return { source: 'teleop', checkpoint: null };
  const worker = alivePayload(heartbeats, 'rollout_service_worker');
  if (POLICY_DRIVING.has(String(worker.state))) {
    const name = worker.checkpoint_name;
    return { source: 'policy', checkpoint: typeof name === 'string' && name ? name : null };
  }
  return null;
}
